import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Save, Trash2, FileText, CheckCircle2,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import RichTextEditor from "../components/RichTextEditor";

export default function AdminPostTemplates() {
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newBody, setNewBody] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/post-templates");
      setTemplates(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const canCreate = newName.trim().length > 0;

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/post-templates", { name: newName.trim(), body: newBody });
      setNewName("");
      setNewBody("");
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-post-templates"
    >
      <Header />
      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <FileText size={16} strokeWidth={2.5} />
            <span className="label-kicker">Blog post templates</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Never start from a blank page.
          </h1>
          <p className="mt-2 text-sm text-[var(--brand-body)]">
            Save a reusable body — headings, placeholder text, whatever your usual match report or transfer rumour looks like — and pick it from "Use Template" when writing a new post.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="post-templates-error"
          >
            {error}
          </p>
        ) : null}

        {/* Add new */}
        <form
          onSubmit={onCreate}
          className="mb-8 rounded-md border border-black/10 bg-[#F8FAFC] p-5"
          data-testid="post-template-create-form"
        >
          <p className="label-kicker text-[var(--brand-body)]">Add a new template</p>
          <label className="mt-3 block">
            <span className="label-kicker text-[var(--brand-body)]">Name</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={80}
              placeholder="Match report"
              data-testid="post-template-new-name"
              className="mt-1 w-full max-w-md rounded-md border border-black/15 bg-white px-3 py-2 text-sm"
            />
          </label>
          <label className="mt-4 block">
            <span className="label-kicker text-[var(--brand-body)]">Placeholder body</span>
            <div className="mt-1">
              <RichTextEditor value={newBody} onChange={setNewBody} minHeight={220} placeholder="e.g. Final score: __-__&#10;&#10;Key moments...&#10;&#10;Man of the match: ..." />
            </div>
          </label>
          <button
            type="submit"
            disabled={!canCreate || creating}
            data-testid="post-template-create-submit"
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add template
          </button>
        </form>

        {/* Existing list */}
        {templates === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : templates.length === 0 ? (
          <p className="text-sm text-[var(--brand-body)]">No templates yet.</p>
        ) : (
          <div className="space-y-4" data-testid="post-templates-list">
            {templates.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                onSaved={refresh}
                setOuterError={setError}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function TemplateRow({ template, onSaved, setOuterError }) {
  const [name, setName] = useState(template.name);
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(template.name);
    setBody(template.body);
  }, [template.name, template.body]);

  const dirty = name.trim() !== template.name || body !== template.body;

  const save = async () => {
    if (!dirty || !name.trim()) return;
    setSaving(true);
    setOuterError("");
    try {
      await api.put(`/admin/post-templates/${template.id}`, { name: name.trim(), body });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete template "${template.name}"? This can't be undone.`)) return;
    setOuterError("");
    try {
      await api.delete(`/admin/post-templates/${template.id}`);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="rounded-md border border-black/10 bg-white p-4"
      data-testid={`post-template-row-${template.id}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          data-testid={`post-template-name-${template.id}`}
          className="min-w-[200px] flex-1 rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-sm font-bold"
        />
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving || !name.trim()}
          data-testid={`post-template-save-${template.id}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save
        </button>
        {saved ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
        <button
          type="button"
          onClick={remove}
          data-testid={`post-template-delete-${template.id}`}
          className="inline-flex items-center gap-1 rounded-md border border-[#E63946]/40 px-2 py-1.5 text-xs text-[#E63946] hover:bg-[#E63946] hover:text-white"
        >
          <Trash2 size={12} />
        </button>
      </div>
      <div className="mt-3">
        <RichTextEditor value={body} onChange={setBody} minHeight={180} />
      </div>
    </div>
  );
}
